import {ChangeDetectionStrategy, Component, computed, input, output, signal} from '@angular/core';
import {STRINGS, SUGGESTIONS} from './assistant.constants';
import {ChatSource} from './chat-stream.service';
import {IconComponent} from './icon.component';
import {ChatBubble} from './markdown.service';

/** Right-aligned bubble. Plain text only (interpolation, pre-wrap): user input is never HTML. */
@Component({
  selector: 'app-aa-user-message',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div class="aa-msg-user"><span class="aa-visually-hidden">You said: </span>{{ text() }}</div>`,
})
export class UserMessageComponent {
  readonly text = input.required<string>();
}

/** Greeting and four suggestions. A suggestion sends immediately. */
@Component({
  selector: 'app-aa-empty-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="aa-empty">
      <img src="assets/imgs/seal.png" alt="" width="56" height="56">
      <h3>{{ strings.emptyTitle }}</h3>
      <p>{{ strings.emptyBody }}</p>
      <ul class="aa-suggestions">
        @for (suggestion of suggestions; track suggestion) {
          <li><button class="aa-suggestion" type="button" (click)="pick.emit(suggestion)">{{ suggestion }}</button></li>
        }
      </ul>
    </div>
  `,
})
export class EmptyStateComponent {
  readonly pick = output<string>();
  protected readonly strings = STRINGS;
  protected readonly suggestions = SUGGESTIONS;
}

const VISIBLE_SOURCES = 4;

/** Compact source links under an answer: icon, truncated title, host in the tooltip, "+N more". */
@Component({
  selector: 'app-aa-source-chips',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <div class="aa-sources">
      <p class="aa-sources-label" [id]="labelId()">{{ strings.sources }}</p>
      <ul class="aa-chips" [attr.aria-labelledby]="labelId()">
        @for (source of visible(); track source.url) {
          <li>
            <a class="aa-chip" [href]="source.url" target="_blank" rel="noopener noreferrer" [title]="host(source.url)">
              <app-aa-icon [name]="isDocument(source.url) ? 'file' : 'globe'"/>
              <span class="aa-chip-title">{{ source.title }}</span>
              <span class="aa-visually-hidden">{{ strings.opensInNewTab }}</span>
            </a>
          </li>
        }
        @if (hiddenCount() > 0) {
          <li><button class="aa-chip aa-chip-more" type="button" (click)="showAll.set(true)">{{ strings.moreSources(hiddenCount()) }}</button></li>
        }
      </ul>
    </div>
  `,
})
export class SourceChipsComponent {
  readonly sources = input.required<readonly ChatSource[]>();
  readonly labelId = input.required<string>();

  protected readonly strings = STRINGS;
  protected readonly showAll = signal(false);
  protected readonly visible = computed(() => this.showAll() ? this.sources() : this.sources().slice(0, VISIBLE_SOURCES));
  protected readonly hiddenCount = computed(() => this.sources().length - this.visible().length);

  protected host(url: string): string {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  }

  protected isDocument(url: string): boolean {
    return /\.(pdf|docx?|xlsx?)(?:$|[?#])/i.test(url);
  }
}

/**
 * Bubbles under an answer (takeBubbles): parcel choices and next steps. A link opens in a new tab
 * like a source chip; any other bubble sends its text, like an empty-state suggestion.
 */
@Component({
  selector: 'app-aa-bubbles',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <ul class="aa-chips aa-bubbles" [attr.aria-label]="strings.nextSteps">
      @for (bubble of bubbles(); track $index) {
        <li>
          @if (bubble.url) {
            <a class="aa-chip" [href]="bubble.url" target="_blank" rel="noopener noreferrer">
              <app-aa-icon name="globe"/>
              <span class="aa-chip-title">{{ bubble.label }}</span>
              <span class="aa-visually-hidden">{{ strings.opensInNewTab }}</span>
            </a>
          } @else {
            <button class="aa-chip aa-chip-more" type="button" [disabled]="disabled()" (click)="pick.emit(bubble.label)">{{ bubble.label }}</button>
          }
        </li>
      }
    </ul>
  `,
})
export class BubblesComponent {
  readonly bubbles = input.required<readonly ChatBubble[]>();
  readonly disabled = input(false);
  readonly pick = output<string>();

  protected readonly strings = STRINGS;
}

/** Copy, thumbs up/down (pressed state), regenerate. */
@Component({
  selector: 'app-aa-message-actions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: {'class': 'aa-actions', '[class.aa-always]': 'always()'},
  template: `
    <button class="aa-icon-btn" type="button" [attr.aria-label]="copied() ? strings.copied : strings.copy" [title]="strings.copy" (click)="copy.emit()">
      <app-aa-icon [name]="copied() ? 'check' : 'copy'"/>
    </button>
    @if (canRate()) {
      <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.helpful" [title]="strings.helpful"
              [attr.aria-pressed]="feedback() === 'up'" (click)="rate.emit('up')">
        <app-aa-icon name="thumbs-up"/>
      </button>
      <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.notHelpful" [title]="strings.notHelpful"
              [attr.aria-pressed]="feedback() === 'down'" (click)="rate.emit('down')">
        <app-aa-icon name="thumbs-down"/>
      </button>
    }
    @if (canRegenerate()) {
      <button class="aa-icon-btn" type="button" [attr.aria-label]="strings.regenerate" [title]="strings.regenerate" (click)="regenerate.emit()">
        <app-aa-icon name="rotate"/>
      </button>
    }
  `,
})
export class MessageActionsComponent {
  readonly feedback = input<'up' | 'down' | null>(null);
  readonly canRate = input(true);
  readonly canRegenerate = input(false);
  readonly always = input(false);
  readonly copied = input(false);

  readonly copy = output<void>();
  readonly rate = output<'up' | 'down'>();
  readonly regenerate = output<void>();

  protected readonly strings = STRINGS;
}
